import React, { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { RefreshCw, Loader2, ChevronDown, ChevronRight, Plus, Trash2, Save, ExternalLink, Info } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Skeleton } from '../ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import api from '../../lib/api';

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(v) || 0);
const fmtK = (v) => { const n = parseFloat(v)||0; return n >= 1000 ? `$${(n/1000).toFixed(1)}k` : fmt(n); };
const CATEGORIES = ['Website Maint', 'SEO', 'Ads Mgmt', 'Listings', 'CRM Support', 'Variable Usage', 'Other'];
const FREQ_LABELS = { week: 'Weekly', month: 'Monthly', year: 'Annual' };
const CAT_COLORS = { 'Website Maint':'bg-blue-50 text-blue-700 border-blue-200', 'SEO':'bg-emerald-50 text-emerald-700 border-emerald-200', 'Ads Mgmt':'bg-violet-50 text-violet-700 border-violet-200', 'Listings':'bg-amber-50 text-amber-700 border-amber-200', 'CRM Support':'bg-pink-50 text-pink-700 border-pink-200', 'Variable Usage':'bg-gray-50 text-gray-600 border-gray-200', 'Other':'bg-gray-50 text-gray-600 border-gray-200' };
const STATUS_COLORS = { active:'bg-emerald-50 text-emerald-700 border-emerald-200', past_due:'bg-red-50 text-red-600 border-red-200', canceled:'bg-gray-100 text-gray-500 border-gray-300' };

function marginBadge(margin, category) {
  const m = parseFloat(margin) || 0;
  let g = 60, a = 40;
  if (category === 'SEO') { g = 70; a = 50; }
  else if (category === 'Ads Mgmt') { g = 40; a = 25; }
  const cls = m >= g ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : m >= a ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 'bg-red-50 text-red-700 border-red-200';
  return <Badge variant="outline" className={`${cls} text-xs tabular-nums`}>{m.toFixed(0)}%</Badge>;
}

// ── Subscription Management Modal ──
function SubManageModal({ open, onOpenChange, subscription, onSave }) {
  const [cat, setCat] = useState('Website Maint');
  const [freq, setFreq] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && subscription) {
      setCat(subscription.category || 'Website Maint');
      setFreq(subscription.frequency || 'month');
      try { setStartDate(subscription.start_date ? format(new Date(subscription.start_date), 'yyyy-MM-dd') : ''); }
      catch { setStartDate(''); }
    }
  }, [open, subscription]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { category: cat, frequency: freq };
      if (startDate) payload.start_date = new Date(startDate + 'T00:00:00Z').toISOString();
      await api.patch(`/api/ops/mrr/${subscription.id}`, payload);
      toast.success('Subscription updated');
      onSave();
      onOpenChange(false);
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  if (!subscription) return null;
  const statusCls = STATUS_COLORS[subscription.status] || STATUS_COLORS.active;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle data-role="heading">{subscription.client_name || 'Subscription'}</DialogTitle>
          <p className="text-xs text-muted-foreground">{subscription.client_email || '\u2014'}</p>
        </DialogHeader>
        <div className="space-y-4 py-3">
          <div className="flex items-center justify-between">
            <Badge variant="outline" className={`${statusCls} text-xs`}>{subscription.status === 'past_due' ? 'Past Due' : subscription.status}</Badge>
            <span className="text-sm font-bold" data-role="mono">{fmt(subscription.amount)}/mo</span>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Category</label>
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Billing Frequency</label>
            <Select value={freq} onValueChange={setFreq}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(FREQ_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
            {freq === 'week' && <p className="text-[10px] text-muted-foreground mt-1">MRR = weekly amount x 52 / 12</p>}
            {freq === 'year' && <p className="text-[10px] text-muted-foreground mt-1">MRR = annual amount / 12</p>}
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Member Since</label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-9 text-sm" />
          </div>

          {subscription.source === 'stripe' && subscription.stripe_subscription_id && (
            <a href={`https://dashboard.stripe.com/subscriptions/${subscription.stripe_subscription_id}`}
              target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-primary hover:underline">
              <ExternalLink className="h-3.5 w-3.5" /> View in Stripe Dashboard
            </a>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Expense Modal ──
function MrrExpenseModal({ open, onOpenChange, subscription, onSaved }) {
  const [exp, setExp] = useState({ labor_cost:0, onboarding_cost:0, monthly_provider_cost:0, software_cost:0, ad_budget:0, notes:'' });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const cat = subscription?.category || '';
  const isAds = cat === 'Ads Mgmt';
  useEffect(() => {
    if (open && subscription?.id) {
      setLoaded(false);
      api.get(`/api/ops/mrr/${subscription.id}/expenses`).then(res => {
        const d = res.data.data;
        setExp({ labor_cost:d?.labor_cost||0, onboarding_cost:d?.onboarding_cost||0, monthly_provider_cost:d?.monthly_provider_cost||0, software_cost:d?.software_cost||0, ad_budget:subscription.ad_budget||0, notes:d?.notes||'' });
        setLoaded(true);
      }).catch(() => setLoaded(true));
    }
  }, [open, subscription]);
  const totalExp = (parseFloat(exp.labor_cost)||0)+(parseFloat(exp.onboarding_cost)||0)+(parseFloat(exp.monthly_provider_cost)||0)+(parseFloat(exp.software_cost)||0);
  const revenue = parseFloat(subscription?.amount)||0;
  const adBudget = isAds ? (parseFloat(exp.ad_budget)||0) : 0;
  const agencyRev = revenue - adBudget;
  const netMrr = agencyRev - totalExp;
  const catHint = cat==='SEO'?'SEO folder':'Ads'==='Ads Mgmt'?'Ads folder':cat==='CRM Support'?'CRM folder':'ClickUp time entries';
  const handleSave = async () => {
    setSaving(true);
    try {
      const p = { labor_cost:parseFloat(exp.labor_cost)||0, onboarding_cost:parseFloat(exp.onboarding_cost)||0, monthly_provider_cost:parseFloat(exp.monthly_provider_cost)||0, software_cost:parseFloat(exp.software_cost)||0, notes:exp.notes };
      if (isAds) p.ad_budget = parseFloat(exp.ad_budget)||0;
      await api.post(`/api/ops/mrr/${subscription.id}/expenses`, p);
      toast.success('Saved'); onOpenChange(false); if (onSaved) onSaved();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 gap-0 flex flex-col" style={{maxHeight:'80vh'}}>
        <DialogHeader className="px-6 pt-5 pb-3"><DialogTitle data-role="heading">Recurring Expense Ledger</DialogTitle><p className="text-xs text-muted-foreground">{subscription?.client_name} <Badge variant="outline" className={`${CAT_COLORS[cat]||''} text-[10px] ml-1`}>{cat}</Badge></p></DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-3">
          {!loaded?<Skeleton className="h-20 w-full"/>:(
            <>
              <div className="border rounded-lg p-4 space-y-3">
                <h3 className="text-[11px] font-semibold uppercase tracking-wide">Monthly Costs</h3>
                {[['Internal Labor (30d)','labor_cost'],['External Provider','monthly_provider_cost'],['Software Overhead','software_cost'],['Onboarding (One-Time)','onboarding_cost']].map(([l,k])=>(
                  <div key={k}><label className="text-xs font-medium text-muted-foreground mb-1 block">{l}</label><Input type="text" inputMode="decimal" value={exp[k]} onChange={e=>setExp(p=>({...p,[k]:e.target.value}))} className="h-8 text-sm text-right" data-role="mono"/></div>
                ))}
                {isAds&&<div className="border-t pt-3"><label className="text-xs font-medium text-amber-700 mb-1 block">Ad Budget (pass-through)</label><Input type="text" inputMode="decimal" value={exp.ad_budget} onChange={e=>setExp(p=>({...p,ad_budget:e.target.value}))} className="h-8 text-sm text-right border-amber-200" data-role="mono"/><p className="text-[10px] text-amber-600 mt-0.5">Excluded from Net MRR</p></div>}
              </div>
              <div className="border rounded-lg p-3 space-y-1 text-xs">
                <div className="flex justify-between"><span className="text-muted-foreground">Total MRR</span><span className="font-semibold" data-role="mono">{fmt(revenue)}/mo</span></div>
                {adBudget > 0 && <div className="flex justify-between text-amber-600"><span>Ad Budget (pass-through)</span><span className="font-medium" data-role="mono">-{fmt(adBudget)}/mo</span></div>}
                {adBudget > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Agency Revenue</span><span className="font-semibold" data-role="mono">{fmt(agencyRev)}/mo</span></div>}
                <div className="flex justify-between"><span className="text-muted-foreground">Expenses</span><span className="font-semibold" data-role="mono">-{fmt(totalExp)}/mo</span></div>
                <div className="flex justify-between border-t pt-1"><span className="font-medium">Net MRR</span><span className={`font-bold ${netMrr>=0?'text-emerald-700':'text-red-600'}`} data-role="mono">{fmt(netMrr)}/mo</span></div>
              </div>
            </>
          )}
        </div>
        <div className="flex-shrink-0 border-t bg-card px-6 py-3 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={()=>onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving}>{saving?<Loader2 className="h-4 w-4 animate-spin mr-1"/>:<Save className="h-4 w-4 mr-1"/>}Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Add Manual Modal ──
function AddManualModal({ open, onOpenChange, onSave }) {
  const [form, setForm] = useState({ client_name:'', client_email:'', amount:'', category:'Website Maint', ad_budget:'' });
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    if (!form.client_name||!form.amount) { toast.error('Name and amount required'); return; }
    setSaving(true);
    try { await onSave({...form, amount:parseFloat(form.amount)||0, ad_budget:parseFloat(form.ad_budget)||0}); onOpenChange(false); setForm({client_name:'',client_email:'',amount:'',category:'Website Maint',ad_budget:''}); } catch {} finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="sm:max-w-sm"><DialogHeader><DialogTitle data-role="heading">Add Manual MRR</DialogTitle></DialogHeader>
      <div className="space-y-3 py-2">
        <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Client Name</label><Input value={form.client_name} onChange={e=>setForm(p=>({...p,client_name:e.target.value}))} className="h-8 text-sm" autoFocus/></div>
        <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Email</label><Input value={form.client_email} onChange={e=>setForm(p=>({...p,client_email:e.target.value}))} className="h-8 text-sm"/></div>
        <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Monthly Amount</label><Input type="text" inputMode="decimal" value={form.amount} onChange={e=>setForm(p=>({...p,amount:e.target.value}))} className="h-8 text-sm text-right" data-role="mono"/></div>
        <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Category</label><Select value={form.category} onValueChange={v=>setForm(p=>({...p,category:v}))}><SelectTrigger className="h-8 text-xs"><SelectValue/></SelectTrigger><SelectContent>{CATEGORIES.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
      </div>
      <DialogFooter><Button variant="outline" size="sm" onClick={()=>onOpenChange(false)}>Cancel</Button><Button size="sm" onClick={handleSave} disabled={saving}>{saving?<Loader2 className="h-4 w-4 animate-spin mr-1"/>:<Plus className="h-4 w-4 mr-1"/>}Add</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}

// ── Main ──
export default function MrrCommandCenter() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [expenseSub, setExpenseSub] = useState(null);
  const [manageSub, setManageSub] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    try { setLoading(true); const res = await api.get('/api/ops/mrr'); setData(res.data); }
    catch { toast.error('Failed to load MRR'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try { const res = await api.post('/api/ops/mrr/sync'); toast.success(`Synced ${res.data.synced} subscriptions`); load(); }
    catch { toast.error('Sync failed'); } finally { setSyncing(false); }
  };

  const handleAddManual = async (entry) => {
    try { await api.post('/api/ops/mrr/manual', entry); toast.success('Added'); load(); }
    catch { toast.error('Failed'); throw new Error(); }
  };

  const handleDelete = async (subId) => {
    try { await api.delete(`/api/ops/mrr/${subId}`); toast.success('Removed'); load(); } catch { toast.error('Failed'); }
  };

  const toggleExpand = (cat) => setExpanded(p => ({...p, [cat]: !p[cat]}));
  const fmtDate = (d) => { if (!d) return '\u2014'; try { return format(new Date(d), 'MM/yy'); } catch { return '\u2014'; } };

  const categories = data?.categories || {};
  const catOrder = CATEGORIES.filter(c => categories[c]);
  const totalGross = data?.total_gross_mrr || 0;
  const totalNet = data?.total_net_mrr || 0;

  return (
    <>
      <Card className="shadow-[var(--shadow-1)]">
        <CardHeader className="pb-2 pt-4 px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold tracking-tight" data-role="heading">MRR</h2>
              <Badge variant="secondary" className="text-xs">{data?.total_subscriptions||0} subscriptions</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="text-xs h-7" onClick={()=>setAddOpen(true)}><Plus className="h-3 w-3 mr-1"/>Manual</Button>
              <Button variant="outline" size="sm" className="text-xs h-7" onClick={handleSync} disabled={syncing}>
                {syncing?<Loader2 className="h-3 w-3 animate-spin mr-1"/>:<RefreshCw className="h-3 w-3 mr-1"/>}Sync Stripe
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-4 sm:px-6 pb-4">
          {loading ? (
            <div className="space-y-3">{[...Array(3)].map((_,i)=><Skeleton key={i} className="h-16 w-full"/>)}</div>
          ) : catOrder.length === 0 ? (
            <div className="p-10 text-center"><p className="text-sm text-muted-foreground">No subscriptions. Click "Sync Stripe".</p></div>
          ) : (
            <div className="space-y-2">
              {catOrder.map(cat => {
                const cd = categories[cat];
                const isOpen = expanded[cat];
                const isAds = cat === 'Ads Mgmt';
                return (
                  <div key={cat} className="border rounded-lg overflow-hidden">
                    <button onClick={()=>toggleExpand(cat)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors">
                      <div className="flex items-center gap-3">
                        {isOpen?<ChevronDown className="h-4 w-4 text-muted-foreground"/>:<ChevronRight className="h-4 w-4 text-muted-foreground"/>}
                        <Badge variant="outline" className={`${CAT_COLORS[cat]||CAT_COLORS.Other} text-xs`}>{cat}</Badge>
                        <span className="text-xs text-muted-foreground">{cd.subscriptions.length} clients</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {cd.avg_margin !== undefined && marginBadge(cd.avg_margin, cat)}
                        <span className="text-xs text-muted-foreground">Net: <span className={`font-semibold ${cd.net_mrr>=0?'text-emerald-700':'text-red-600'}`} data-role="mono">{fmt(cd.net_mrr)}</span></span>
                        <span className="text-sm font-bold" data-role="mono">{fmt(cd.gross_mrr)}/mo</span>
                      </div>
                    </button>
                    {isOpen && (
                      <div className="border-t overflow-x-auto">
                        <Table>
                          <TableHeader><TableRow className="hover:bg-transparent">
                            <TableHead className="text-xs font-semibold uppercase min-w-[180px]">Client</TableHead>
                            <TableHead className="text-xs font-semibold uppercase text-right w-[110px]">MRR</TableHead>
                            {isAds&&<TableHead className="text-xs font-semibold uppercase text-right w-[100px]"><span className="text-amber-600">Ad Budget</span></TableHead>}
                            <TableHead className="text-xs font-semibold uppercase text-right w-[100px]">Net MRR</TableHead>
                            <TableHead className="text-xs font-semibold uppercase text-right w-[80px]">Margin</TableHead>
                            
                              <TableHead className="text-xs font-semibold uppercase text-right w-[90px]">ACV</TableHead>
                              <TableHead className="text-xs font-semibold uppercase text-right w-[90px]">
                                <TooltipProvider><Tooltip><TooltipTrigger className="inline-flex items-center gap-0.5">TCS <Info className="h-2.5 w-2.5"/></TooltipTrigger><TooltipContent>Total Customer Spend (lifetime)</TooltipContent></Tooltip></TooltipProvider>
                              </TableHead>
                              <TableHead className="text-xs font-semibold uppercase text-right w-[90px]">LTV (3yr)</TableHead>
                            
                            <TableHead className="w-[40px]"/>
                          </TableRow></TableHeader>
                          <TableBody>
                            {cd.subscriptions.map(sub => {
                              const mrr = parseFloat(sub.amount)||0;
                              const adBudget = parseFloat(sub.ad_budget)||0;
                              const agencyRev = parseFloat(sub.agency_revenue ?? (mrr - adBudget))||0;
                              const netMrr = parseFloat(sub.net_mrr)||0;
                              const spent = parseFloat(sub.total_spent)||0;
                              return (
                                <TableRow key={sub.id} className="hover:bg-[var(--row-hover)]">
                                  <TableCell>
                                    <button onClick={()=>setManageSub(sub)} className="text-sm font-medium text-primary hover:underline text-left">
                                      {sub.client_name||sub.client_email||'\u2014'}
                                    </button>
                                    <div className="flex items-center gap-1.5 mt-0.5">
                                      <Badge variant="outline" className={`${CAT_COLORS[sub.category]||''} text-[9px]`}>{sub.category}</Badge>
                                      <span className="text-[9px] text-muted-foreground">{FREQ_LABELS[sub.frequency||'month']}</span>
                                      {sub.start_date && <span className="text-[9px] text-muted-foreground">Since {fmtDate(sub.start_date)}</span>}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <button onClick={()=>setExpenseSub(sub)} className="text-sm font-bold text-primary hover:underline" data-role="mono">{fmt(mrr)}</button>
                                  </TableCell>
                                  {isAds&&<TableCell className="text-right"><span className="text-sm text-amber-600 font-semibold" data-role="mono">{fmt(adBudget)}</span></TableCell>}
                                  <TableCell className="text-right">
                                    <span className={`text-sm font-semibold ${netMrr>=0?'text-emerald-700':'text-red-600'}`} data-role="mono">{fmt(netMrr)}</span>
                                  </TableCell>
                                  <TableCell className="text-right">{marginBadge(sub.margin, cat)}</TableCell>
                                  
                                    <TableCell className="text-right"><span className="text-sm" data-role="mono">{fmtK(agencyRev*12)}</span></TableCell>
                                    <TableCell className="text-right"><span className="text-sm" data-role="mono">{spent>0?fmtK(spent):'\u2014'}</span></TableCell>
                                    <TableCell className="text-right"><span className="text-sm text-violet-700 font-medium" data-role="mono">{fmtK(agencyRev*36)}</span></TableCell>
                                  
                                  <TableCell>
                                    {sub.source==='manual'&&<Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground hover:text-red-500" onClick={()=>handleDelete(sub.id)}><Trash2 className="h-3 w-3"/></Button>}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {catOrder.length > 0 && (
            <div className="mt-4 flex items-center justify-between bg-muted/40 rounded-lg px-4 py-3">
              <div className="flex items-center gap-5 text-xs">
                <span className="text-muted-foreground">Gross: <span className="font-bold text-foreground text-sm" data-role="mono">{fmt(totalGross)}/mo</span></span>
                <span className="text-muted-foreground">Net: <span className={`font-bold text-sm ${totalNet>=0?'text-emerald-700':'text-red-600'}`} data-role="mono">{fmt(totalNet)}/mo</span></span>
                <span className="text-muted-foreground">{data?.total_subscriptions||0} active</span>
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <div className="text-right">
                      <span className="text-[10px] text-muted-foreground block">Annual Revenue</span>
                      <span className="text-lg font-extrabold text-primary" data-role="mono">{fmtK(totalGross * 12)}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs space-y-0.5">
                    <p>Gross ARR: <span className="font-semibold" data-role="mono">{fmt(totalGross * 12)}</span></p>
                    <p>Net ARR: <span className={`font-semibold ${totalNet>=0?'text-emerald-400':'text-red-400'}`} data-role="mono">{fmt(totalNet * 12)}</span></p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
        </CardContent>
      </Card>

      <SubManageModal open={!!manageSub} onOpenChange={o=>{if(!o)setManageSub(null)}} subscription={manageSub} onSave={load}/>
      <MrrExpenseModal open={!!expenseSub} onOpenChange={o=>{if(!o)setExpenseSub(null)}} subscription={expenseSub} onSaved={load}/>
      <AddManualModal open={addOpen} onOpenChange={setAddOpen} onSave={handleAddManual}/>
    </>
  );
}
